import React, { useState } from 'react';
import { 
  ChevronRight, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  CheckCircle,
  AlertCircle,
  Navigation,
  Globe,
  Users,
  Building2,
  Award
} from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    setTimeout(() => {
      setLoading(false);
      setFormStatus({
        submitted: true,
        success: true,
        message: 'Thank you for contacting us! We will get back to you soon.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          success: false,
          message: ''
        });
      }, 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Number",
      content: ["+91-9628362187", "+91-7275370808"],
      action: "tel:+919628362187",
      bgColor: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      icon: Mail,
      title: "Email Address",
      content: ["rdspharmacycollege@gmail.com"],
      action: "mailto:rdspharmacycollege@gmail.com",
      bgColor: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      icon: MapPin,
      title: "Our Address",
      content: ["Kusanw Bhaupur Jaunpur UP-222136", "(Near Trilochan Mahadev)"],
      action: "https://maps.google.com/?q=Kusanw+Bhaupur+Jaunpur",
      bgColor: "bg-red-100",
      iconColor: "text-red-600"
    },
    {
      icon: Clock,
      title: "Working Hours",
      content: ["Daily: 9:00 AM - 7:00 PM"],
      bgColor: "bg-purple-100",
      iconColor: "text-purple-600"
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook", color: "hover:bg-blue-600" },
    { icon: Twitter, href: "#", label: "Twitter", color: "hover:bg-sky-500" },
    { icon: Instagram, href: "#", label: "Instagram", color: "hover:bg-pink-600" },
    { icon: Linkedin, href: "#", label: "LinkedIn", color: "hover:bg-blue-700" },
    { icon: Youtube, href: "#", label: "YouTube", color: "hover:bg-red-600" }
  ];

  return (
    <div className="bg-white">
      <div className="relative bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-blue-200 mb-4">
            <span className="hover:text-orange-400 cursor-pointer">Home</span>
            <ChevronRight size={16} />
            <span className="text-orange-400 font-semibold">Contact Us</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-blue-100 max-w-2xl">Get in touch with us for any queries or assistance</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactInfo.map((info, index) => (
            <a
              key={index}
              href={info.action}
              target={info.action?.startsWith('http') ? '_blank' : undefined}
              rel={info.action?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <div className={`inline-flex p-3 ${info.bgColor} rounded-full mb-3 group-hover:scale-110 transition-transform duration-300`}>
                <info.icon className={`w-6 h-6 ${info.iconColor}`} />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{info.title}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                {info.content.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </a>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-2">Get In Touch</h2>
            <p className="text-gray-600 mb-8">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
            
            {formStatus.submitted && formStatus.success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">{formStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all duration-300 resize-none"
                  placeholder="Your message here..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Our Location</h2>
              <div className="aspect-video bg-gray-200 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28823.123456789!2d82.123456!3d25.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDA3JzQyLjQiTiA4MsKwMTUnMjMuNSJF!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                  title="College Location"
                ></iframe>
              </div>
              
              <div className="mt-6 flex items-start space-x-3 text-gray-600">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900">Visit Us:</p>
                  <p className="text-sm">Kusanw Bhaupur Jaunpur UP-222136</p>
                  <p className="text-sm">(Near Trilochan Mahadev)</p>
                </div>
              </div>
              
              <a
                href="https://maps.google.com/?q=Kusanw+Bhaupur+Jaunpur"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center space-x-2 text-orange-500 hover:text-orange-600 font-semibold text-sm"
              >
                <Navigation className="w-4 h-4" />
                <span>Get Directions</span>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                <Building2 className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-semibold text-blue-900 mb-1">Established</h3>
                <p className="text-2xl font-bold text-blue-800">1995</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                <Users className="w-8 h-8 text-orange-600 mb-2" />
                <h3 className="font-semibold text-orange-900 mb-1">Students</h3>
                <p className="text-2xl font-bold text-orange-800">5000+</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                <Globe className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="font-semibold text-green-900 mb-1">Courses</h3>
                <p className="text-2xl font-bold text-green-800">15+</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                <Award className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-semibold text-purple-900 mb-1">Faculty</h3>
                <p className="text-2xl font-bold text-purple-800">50+</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Connect With Us</h2>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 ${social.color} hover:text-white transition-all duration-300 transform hover:scale-110`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: "What are the admission hours?",
                a: "Our admission office is open from 9:00 AM to 5:00 PM on weekdays and 9:00 AM to 1:00 PM on Saturdays."
              },
              {
                q: "Is there a campus tour available?",
                a: "Yes, we offer campus tours every Friday at 11:00 AM. Please contact our office to schedule your visit."
              },
              {
                q: "How can I get my query resolved quickly?",
                a: "You can call us directly at +91-9628362187 during working hours for immediate assistance."
              },
              {
                q: "Do you have hostel facilities?",
                a: "Yes, we have separate hostel facilities for boys and girls. Contact our admission office for more details."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-semibold text-blue-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for updates on admissions, events, and announcements
          </p>
          <form className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button className="bg-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-300">
              Subscribe
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;